import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import { ActivityNote } from '@/experiences/sales-portal/components/activity-notes/types';
import { createStyles } from '@/contexts/theme-context';
import { CRM_CHECK_IN_INTERACTION_LABEL_KEYS } from '@/experiences/sales-portal/blocs/utils/crm-check-in-interactions';
import { formatSummaryDate, formatTime, getDeviceTimezone } from '@/utils/date';

export const NOTE_MAX_LINES = 3;

/**
 * Formats an activity note timestamp for display on a note card, combining the
 * summary date and time into a single string (e.g. "Jan 1, 12:00pm").
 *
 * Notes read in the viewer's own timezone rather than the account's, so a rep
 * covering several regions sees every note using their own timezone.
 *
 * @param timestamp - ISO 8601 timestamp from the activity note.
 * @returns The formatted & localized "<date>, <time>" string.
 */
export const formatNoteTimestamp = (timestamp: string): string => {
  const asMoment = moment.tz(timestamp, getDeviceTimezone());

  return i18n.t('sales_portal.activity_notes.timestamp', {
    date: formatSummaryDate(asMoment),
    time: formatTime(asMoment),
  });
};

/**
 * Builds the author/date line rendered under a note's content as a single
 * string to prevent wrapping
 *
 * @param note - The activity note to describe.
 * @returns The localized metadata line.
 */
export const formatNoteMetadata = (note: ActivityNote): string => {
  const timestamp = formatNoteTimestamp(note.createdAt);

  if (!note.authorName) {
    return timestamp;
  }

  return i18n.t('sales_portal.activity_notes.metadata', {
    author: note.authorName,
    timestamp,
  });
};

export interface NoteReminder {
  accessibilityLabel: string;
  text: string;
}

/**
 * Builds the reminder badge text and its accessibility label from one shared
 * timestamp so the two can never disagree.
 *
 * @param note - The activity note to read the reminder from.
 * @returns The reminder strings, or null when the note has no reminder set.
 */
export const formatNoteReminder = (note: ActivityNote): NoteReminder | null => {
  if (!note.reminderDate) {
    return null;
  }

  const text = formatNoteTimestamp(note.reminderDate);

  return {
    accessibilityLabel: i18n.t('sales_portal.activity_notes.reminder_label', {
      timestamp: text,
    }),
    text,
  };
};

export interface VisitPill {
  key: string;
  label: string;
}

/**
 * Builds the Check in Visit pill and one pill for each check-in tag.
 *
 * @param note - The activity note to read the check-in tags from.
 * @returns The pills, or an empty list when the note is not a check-in.
 */
export const formatVisitPills = (note: ActivityNote): VisitPill[] =>
  note.interactions === null
    ? []
    : [
        { key: 'visit', label: i18n.t('sales_portal.activity_notes.visit') },
        ...note.interactions.map((interaction) => ({
          key: interaction,
          label: i18n.t(CRM_CHECK_IN_INTERACTION_LABEL_KEYS[interaction]),
        })),
      ];

export const useActivityNoteStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    gap: theme.sk.spacing.spaceSmall,
    padding: theme.sk.spacing.spaceSMedium,
  },
  loadingIndicator: {
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  metadataRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
  },
  notesList: {
    gap: theme.sk.spacing.spaceSmall,
  },
  pillRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  sectionHeader: {
    marginBottom: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceXsmall,
  },
  // Desktop keeps the top padding every other section header on this page uses;
  // the mobile layout relies on the content column's gap instead.
  sectionHeaderDesktop: {
    paddingTop: theme.sk.spacing.spaceSMedium,
  },
}));
